import { useState, useEffect } from 'react';
import { Site } from '../types';
import { callSiteApi } from '../utils';
import { Image as ImageIcon, ChevronLeft, ChevronRight, Edit3, Trash2, CheckSquare, Square, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { PostEditModal } from './PostEditModal';

interface PostsListProps {
  site: Site;
  postType: string;
  searchTerm: string;
  statusFilter: string;
  refreshTrigger: boolean;
}

export function PostsList({ site, postType, searchTerm, statusFilter, refreshTrigger }: PostsListProps) {
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const { toast } = useToast();

  const [editingPost, setEditingPost] = useState<any | null>(null);
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isBulkActing, setIsBulkActing] = useState(false);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        post_type: postType,
        status: statusFilter,
        page: page.toString(),
        per_page: '20',
        s: searchTerm
      }).toString();
      
      const data = await callSiteApi(site.url, site.publicKey, site.secretKey, `content/posts?${qs}`);
      if (data && data.posts) {
        setPosts(data.posts);
        setTotalPages(data.total_pages);
        setTotalPosts(data.total_posts);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast({ title: 'Error', description: 'Failed to load content.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset page if search or status changes
    setPage(1);
  }, [searchTerm, statusFilter, postType]);

  useEffect(() => {
    fetchPosts();
    // clear selection on new fetch
    setSelectedIds([]);
  }, [site.id, postType, searchTerm, statusFilter, page, refreshTrigger]);

  const handleStatusChange = async (postId: number, newStatus: string) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, status: newStatus } : p));
    try {
      await callSiteApi(
        site.url, site.publicKey, site.secretKey, 
        `content/posts/${postId}`, 'PUT', 
        JSON.stringify({ post_status: newStatus })
      );
      toast({ title: 'Status updated' });
    } catch (error) {
      toast({ title: 'Update failed', variant: 'destructive' });
      fetchPosts(); // revert
    }
  };

  const handleBulkAction = async (action: string, value?: string) => {
    if (selectedIds.length === 0) return;
    setIsBulkActing(true);
    try {
      await callSiteApi(
        site.url, site.publicKey, site.secretKey, 
        'content/posts/bulk', 'POST', 
        JSON.stringify({ post_ids: selectedIds, action, value })
      );
      toast({ title: 'Bulk action completed' });
      setSelectedIds([]);
      fetchPosts();
    } catch (error) {
      toast({ title: 'Bulk action failed', variant: 'destructive' });
    } finally {
      setIsBulkActing(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === posts.length && posts.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(posts.map(p => p.id));
    }
  };

  const toggleSelect = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(prev => prev.filter(pid => pid !== id));
    } else {
      setSelectedIds(prev => [...prev, id]);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'publish': return 'bg-emerald-100/60 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/40 w-[60px] justify-center';
      case 'draft': return 'bg-amber-100/60 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 border-amber-200 dark:border-amber-800/40 w-[60px] justify-center';
      case 'trash': return 'bg-red-100/60 text-red-700 dark:bg-red-900/20 dark:text-red-400 border-red-200 dark:border-red-800/40 w-[60px] justify-center';
      case 'pending': return 'bg-blue-100/60 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border-blue-200 dark:border-blue-800/40 w-[60px] justify-center';
      case 'private': return 'bg-purple-100/60 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 border-purple-200 dark:border-purple-800/40 w-[60px] justify-center';
      default: return 'bg-muted text-muted-foreground border-border w-[60px] justify-center';
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden flex flex-col">
      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="bg-primary/5 border-b border-primary/20 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          <span className="text-sm font-medium text-foreground">
            {selectedIds.length} item{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button 
              disabled={isBulkActing}
              onClick={() => handleBulkAction('status', 'publish')}
              className="text-xs px-3 py-1.5 rounded-md bg-emerald-500/10 text-emerald-600 font-medium hover:bg-emerald-500/20 transition-colors"
            >
              Set Publish
            </button>
            <button 
               disabled={isBulkActing}
               onClick={() => handleBulkAction('status', 'draft')}
               className="text-xs px-3 py-1.5 rounded-md bg-amber-500/10 text-amber-600 font-medium hover:bg-amber-500/20 transition-colors"
            >
              Set Draft
            </button>
            <button 
               disabled={isBulkActing}
               onClick={() => handleBulkAction('trash')}
               className="text-xs px-3 py-1.5 rounded-md bg-red-500/10 text-red-600 font-medium hover:bg-red-500/20 transition-colors flex items-center gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" /> Move to Trash
            </button>
          </div>
        </div>
      )}

      {/* Table Header */}
      <div className="grid grid-cols-[auto_1fr_120px_150px_150px_80px] gap-4 items-center px-6 py-3 border-b border-border bg-muted/30 text-xs font-semibold text-muted-foreground p-0">
        <div className="flex items-center gap-4">
          <button onClick={toggleSelectAll} className="text-muted-foreground hover:text-foreground">
            {selectedIds.length === posts.length && posts.length > 0 ? (
              <CheckSquare className="w-4 h-4" />
            ) : (
              <Square className="w-4 h-4" />
            )}
          </button>
          <div className="w-[40px]">Media</div>
        </div>
        <div>Title & Slug</div>
        <div>Status</div>
        <div>Date Modified</div>
        <div>Author</div>
        <div className="text-right">Actions</div>
      </div>

      <div className="divide-y divide-border">
        {loading && posts.length === 0 ? (
          <div className="flex flex-col gap-3 p-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-muted/30 animate-pulse rounded-xl" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center bg-card">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Edit3 className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-bold">No Content Found</h3>
            <p className="text-muted-foreground text-sm max-w-sm mt-1">
              There are no items matching your criteria. Try adjusting your search or filters.
            </p>
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.id} className="grid grid-cols-[auto_1fr_120px_150px_150px_80px] gap-4 items-center px-6 py-3.5 hover:bg-muted/50 transition-colors group">
              <div className="flex items-center gap-4">
                <button onClick={() => toggleSelect(post.id)} className="text-muted-foreground hover:text-foreground">
                   {selectedIds.includes(post.id) ? (
                     <CheckSquare className="w-4 h-4" />
                   ) : (
                     <Square className="w-4 h-4 opacity-50 group-hover:opacity-100" />
                   )}
                </button>
                <div 
                  className="w-10 h-10 rounded overflow-hidden bg-muted flex items-center justify-center border border-border/50 cursor-pointer relative"
                  onClick={() => setEditingPost(post)}
                >
                  {post.featured_image_url ? (
                    <img src={post.featured_image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-muted-foreground/50" />
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Edit3 className="w-3.5 h-3.5 text-white" />
                  </div>
                </div>
              </div>

              <div className="min-w-0 pr-4">
                <button 
                  onClick={() => setEditingPost(post)}
                  className="text-sm font-semibold text-foreground truncate hover:text-workspace-accent transition-colors block text-left"
                >
                  {post.title || '(No Title)'}
                </button>
                <p className="text-[11px] text-muted-foreground truncate font-mono mt-0.5">/{post.slug}</p>
              </div>

              <div>
                <select 
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border cursor-pointer outline-none appearance-none text-center ${getStatusColor(post.status)}`}
                  value={post.status}
                  onChange={(e) => handleStatusChange(post.id, e.target.value)}
                >
                  <option value="publish">PUBLISH</option>
                  <option value="draft">DRAFT</option>
                  <option value="pending">PENDING</option>
                  <option value="private">PRIVATE</option>
                  <option value="trash">TRASH</option>
                </select>
              </div>

              <div className="text-[11px] text-muted-foreground truncate">
                {post.modified?.split(' ')[0]}
              </div>

              <div className="text-[11px] font-medium text-foreground truncate">
                {post.author}
              </div>

              <div className="flex justify-end">
                <a
                  href={post.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  title="View Page"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-border flex items-center justify-between bg-card text-sm">
          <span className="text-muted-foreground">Showing {(page - 1) * 20 + 1} to Math.min(page * 20, totalPosts) of {totalPosts}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1 rounded hover:bg-muted disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2 font-medium">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1 rounded hover:bg-muted disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {editingPost && (
         <PostEditModal
           site={site}
           post={editingPost}
           postType={postType}
           onClose={() => setEditingPost(null)}
           onSave={() => {
             setEditingPost(null);
             fetchPosts();
           }}
         />
      )}
    </div>
  );
}
